import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import Select from 'react-select';
import { toast } from 'react-toastify';
import styles from './TimetableEntry.module.css';

const TimetableEntry = ({ 
  configuration, 
  classesData, 
  teachersData,
  onSave 
}) => {
  const { control, handleSubmit, watch, reset, formState: { errors } } = useForm();
  
  const [sectionsData, setSectionsData] = useState([]);
  const [subjectsData, setSubjectsData] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem("token");
  const centerId = localStorage.getItem("centerId");
  const headers = { Authorization: `Bearer ${token}` };

  const selectedClassWatch = watch('classId');
  const selectedSectionWatch = watch('classSectionId');

  useEffect(() => {
    if (selectedClassWatch) {
      fetchSections(selectedClassWatch.value);
    }
  }, [selectedClassWatch]);

  useEffect(() => {
    if (selectedSectionWatch) {
      fetchSubjects(selectedSectionWatch.value);
    }
  }, [selectedSectionWatch]);

  const fetchSections = async (classId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}ClassSection/GetAllSectionByCenterClassIdAndCenterIds?centerClassId=${classId}&centerId=${centerId}`,
        { headers }
      );
      const data = await response.json();
      setSectionsData(data || []);
    } catch (error) {
      console.error("Error fetching sections:", error);
    }
  };

  const fetchSubjects = async (classSectionId) => {
    try {
      console.log("url", `${process.env.REACT_APP_API_URL}StudentEvaluation/GetSubjectByClassSectionId?ClassSectionId=${classSectionId}`)
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}StudentEvaluation/GetSubjectByClassSectionId?ClassSectionId=${classSectionId}`,
        { headers }
      );
      const data = await response.json();
      setSubjectsData(data.subjects || []);
    } catch (error) {
      console.error("Error fetching subjects:", error);
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      
      const entryData = {
        configurationId: configuration.id,
        classSectionId: data.classSectionId.value,
        subjectId: data.subjectId.value,
        teacherId: data.teacherId.value,
        dayOfWeek: parseInt(data.dayOfWeek.value),
        periodNumber: parseInt(data.periodNumber.value),
        roomNumber: data.roomNumber || ''
      };

      await onSave(entryData);
      
      reset();
      setSelectedClass(null);
      setSelectedSection(null);
      setSectionsData([]);
      setSubjectsData([]);
      
      toast.success('Timetable entry added successfully!');
    } catch (error) {
      console.error('Error saving entry:', error);
      toast.error(error.message || 'Aleady added');
    } finally {
      setLoading(false);
    }
  };

  const classOptions = classesData.map(c => ({
    value: c.id,
    label: c.name
  }));

  const sectionOptions = sectionsData.map(s => ({
    value: s.id,
    label: s.name
  }));

  const subjectOptions = subjectsData.map(s => ({
    value: s.id,
    label: s.name
  }));

  const teacherOptions = teachersData.map(t => ({
    value: t.id,
    label: t.firstName + t.lastName
  }));

  const dayOptions = [
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
    { value: 7, label: 'Sunday' }
  ].slice(0, configuration?.daysPerWeek || 6);

  const periodOptions = configuration?.periods
    ?.filter(p => !p.isBreak)
    ?.map(p => ({
      value: p.periodNumber,
      label: `Period ${p.periodNumber} (${p.startTime} - ${p.endTime})`
    })) || [];

  return (
    <div className={styles.entryContainer}>
      <h3>Add Timetable Entry</h3>

      <form onSubmit={handleSubmit(onSubmit)} className={styles.entryForm}>
        <div className={styles.formGrid}>
          <div className={styles.formGroup}>
            <label>Class *</label>
            <Controller
              name="classId"
              control={control}
              rules={{ required: 'Class is required' }}
              render={({ field }) => (
                <Select
                  {...field}
                  options={classOptions}
                  placeholder="Select Class"
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              )}
            />
            {errors.classId && <span className={styles.error}>{errors.classId.message}</span>}
          </div>

          <div className={styles.formGroup}>
            <label>Section *</label>
            <Controller
              name="classSectionId"
              control={control}
              rules={{ required: 'Section is required' }}
              render={({ field }) => (
                <Select
                  {...field}
                  options={sectionOptions}
                  placeholder="Select Section"
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isDisabled={!selectedClassWatch}
                />
              )}
            />
            {errors.classSectionId && <span className={styles.error}>{errors.classSectionId.message}</span>}
          </div>

          <div className={styles.formGroup}>
            <label>Subject *</label>
            <Controller
              name="subjectId"
              control={control}
              rules={{ required: 'Subject is required' }}
              render={({ field }) => (
                <Select
                  {...field}
                  options={subjectOptions}
                  placeholder="Select Subject"
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isDisabled={!selectedSectionWatch}
                />
              )}
            />
            {errors.subjectId && <span className={styles.error}>{errors.subjectId.message}</span>}
          </div>

          <div className={styles.formGroup}>
            <label>Teacher *</label>
            <Controller
              name="teacherId"
              control={control}
              rules={{ required: 'Teacher is required' }}
              render={({ field }) => (
                <Select
                  {...field}
                  options={teacherOptions}
                  placeholder="Select Teacher"
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              )}
            />
            {errors.teacherId && <span className={styles.error}>{errors.teacherId.message}</span>}
          </div>

          <div className={styles.formGroup}>
            <label>Day *</label>
            <Controller
              name="dayOfWeek"
              control={control}
              rules={{ required: 'Day is required' }}
              render={({ field }) => (
                <Select
                  {...field}
                  options={dayOptions}
                  placeholder="Select Day"
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              )}
            />
            {errors.dayOfWeek && <span className={styles.error}>{errors.dayOfWeek.message}</span>}
          </div>

          <div className={styles.formGroup}>
            <label>Period *</label>
            <Controller
              name="periodNumber"
              control={control}
              rules={{ required: 'Period is required' }}
              render={({ field }) => (
                <Select
                  {...field}
                  options={periodOptions}
                  placeholder="Select Period"
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              )}
            />
            {errors.periodNumber && <span className={styles.error}>{errors.periodNumber.message}</span>}
          </div>

          <div className={styles.formGroup}>
            <label>Room Number (Optional)</label>
            <Controller
              name="roomNumber"
              control={control}
              render={({ field }) => (
                <input 
                  {...field} 
                  type="text" 
                  placeholder="e.g., Room 101"
                  className={styles.input}
                />
              )}
            />
          </div>
        </div>

        <div className={styles.formActions}>
          <button 
            type="submit" 
            className={styles.saveButton}
            disabled={loading}
          >
            {loading ? 'Adding...' : 'Add Entry'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TimetableEntry;